import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenantFormat } from '../hooks/useTenantFormat';
import { useToast } from '../hooks/useToast';
import { api } from '../lib/api';
import { GlassCard } from '../components/GlassCard';
import { AlertTriangle, TrendingDown, Search, ArrowUpDown } from 'lucide-react';

const BUCKETS = [
  { key: '0-30', label: '0–30 days', color: 'emerald', bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700' },
  { key: '31-60', label: '31–60 days', color: 'yellow', bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700' },
  { key: '61-90', label: '61–90 days', color: 'orange', bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700' },
  { key: '90+', label: '90+ days', color: 'red', bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700' },
];

export default function Outstanding() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { formatMoney } = useTenantFormat();

  const [tab, setTab] = useState('receivables');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState('total_outstanding');
  const [sortDir, setSortDir] = useState('desc');

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const fn = tab === 'receivables' ? api.outstandingReceivables : api.outstandingPayables;
      const r = await fn(token);
      setData(r);
    } catch (e) {
      if (e.status === 403) {
        setData({ _upgrade: true });
      } else if (e.status !== 401) {
        toast.show(e.message || 'Failed to load', 'error');
      }
    } finally { setLoading(false); }
  }, [token, tab, toast]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    let r = [...(data?.rows || [])];
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter(row => {
        const name = tab === 'receivables' ? row.customer_name : row.vendor_name;
        return (name || '').toLowerCase().includes(q);
      });
    }
    r.sort((a, b) => {
      let aVal, bVal;
      if (sortCol === 'name') {
        aVal = (tab === 'receivables' ? a.customer_name : a.vendor_name) || '';
        bVal = (tab === 'receivables' ? b.customer_name : b.vendor_name) || '';
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      if (BUCKETS.some(b2 => b2.key === sortCol)) {
        aVal = a.buckets?.[sortCol] || 0;
        bVal = b.buckets?.[sortCol] || 0;
      } else {
        aVal = a[sortCol] || 0;
        bVal = b[sortCol] || 0;
      }
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return r;
  }, [data, search, sortCol, sortDir, tab]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  const SortHead = ({ col, children, align }) => (
    <th className={`${align === 'right' ? 'text-right' : 'text-left'} px-4 py-3 text-gray-500 text-xs font-bold uppercase cursor-pointer select-none hover:text-gray-700 transition-colors`}
      onClick={() => toggleSort(col)}>
      <span className="inline-flex items-center gap-1">
        {children}
        {sortCol === col && <ArrowUpDown size={10} className={sortDir === 'asc' ? 'rotate-180' : ''} />}
      </span>
    </th>
  );

  const isReceivables = tab === 'receivables';

  if (data?._upgrade) {
    return (
      <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="outstanding-page">
        <GlassCard className="p-12 text-center">
          <AlertTriangle size={40} className="text-yellow-500 mx-auto mb-3" />
          <h2 className="text-gray-900 font-extrabold text-lg mb-2">Premium Feature</h2>
          <p className="text-gray-500 text-sm">Outstanding receivables & payables tracking requires a Pro or Premium plan.</p>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="outstanding-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center">
            <TrendingDown size={20} className="text-amber-600" />
          </div>
          <div>
            <h1 className="text-gray-900 font-extrabold text-xl">Outstanding</h1>
            {data?.as_of_date && <p className="text-gray-400 text-xs">As of {new Date(data.as_of_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>}
          </div>
        </div>

        {/* Toggle */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1" data-testid="outstanding-toggle">
          <button onClick={() => { setTab('receivables'); setSearch(''); }}
            data-testid="outstanding-tab-receivables"
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${tab === 'receivables' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            Receivables
          </button>
          <button onClick={() => { setTab('payables'); setSearch(''); }}
            data-testid="outstanding-tab-payables"
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${tab === 'payables' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            Payables
          </button>
        </div>
      </div>

      {/* Bucket summary cards */}
      {loading ? (
        <div className="grid grid-cols-5 gap-3 mb-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-24 rounded-xl bg-gray-100 animate-pulse" />
          ))}
        </div>
      ) : data?.totals && (
        <div className="grid grid-cols-5 gap-3 mb-5">
          {BUCKETS.map(b => (
            <GlassCard key={b.key} className={`p-4 ${b.bg} ${b.border}`} data-testid={`bucket-${b.key}`}>
              <p className={`text-xs font-bold uppercase ${b.text} opacity-70`}>{b.label}</p>
              <p className={`text-lg font-extrabold tabular-nums mt-1 ${b.text}`}>{formatMoney(data.totals[b.key] || 0)}</p>
            </GlassCard>
          ))}
          <GlassCard className="p-4 bg-gray-900 border-gray-700" data-testid="bucket-total">
            <p className="text-xs font-bold uppercase text-gray-400">Total</p>
            <p className="text-lg font-extrabold tabular-nums mt-1 text-white">{formatMoney(data.totals.total || 0)}</p>
            <p className="text-gray-500 text-[10px] mt-0.5">{(data.rows || []).length} {isReceivables ? 'customers' : 'vendors'}</p>
          </GlassCard>
        </div>
      )}

      {/* Search */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder={`Search ${isReceivables ? 'customer' : 'vendor'}...`}
            value={search} onChange={e => setSearch(e.target.value)}
            data-testid="outstanding-search"
            className="w-full h-10 pl-9 pr-3 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm focus:border-brand-purple outline-none" />
        </div>
        <span className="text-gray-400 text-xs ml-auto">{rows.length} {isReceivables ? 'customer' : 'vendor'}{rows.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Table */}
      <GlassCard className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="outstanding-table">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <SortHead col="name">{isReceivables ? 'Customer' : 'Vendor'}</SortHead>
                {isReceivables && <th className="text-left px-4 py-3 text-gray-500 text-xs font-bold uppercase">Phone</th>}
                {BUCKETS.map(b => (
                  <SortHead key={b.key} col={b.key} align="right">{b.label}</SortHead>
                ))}
                <SortHead col="total_outstanding" align="right">Total</SortHead>
                {isReceivables && <SortHead col="unpaid_invoice_count" align="right">Unpaid #</SortHead>}
                <th className="text-right px-4 py-3 text-gray-500 text-xs font-bold uppercase w-16" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    {Array.from({ length: isReceivables ? 9 : 7 }).map((_, j) => <td key={j} className="px-4 py-3"><div className="h-4 w-16 bg-gray-100 rounded animate-pulse" /></td>)}
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={isReceivables ? 9 : 7} className="py-16 text-center" data-testid="outstanding-empty">
                    <p className="text-2xl mb-2">🎉</p>
                    <p className="text-gray-500 text-sm font-semibold">You're all caught up!</p>
                    <p className="text-gray-400 text-xs mt-1">No outstanding {isReceivables ? 'receivables' : 'payables'}.</p>
                  </td>
                </tr>
              ) : rows.map(row => {
                const name = isReceivables ? row.customer_name : row.vendor_name;
                const phone = isReceivables ? row.customer_phone : row.vendor_phone;
                const key = isReceivables ? row.customer_id : row.vendor_key;
                const path = isReceivables ? `/outstanding/customers/${key}` : `/outstanding/vendors/${encodeURIComponent(key)}`;
                return (
                  <tr key={key} data-testid={`orow-${key}`}
                    className="border-b border-gray-100 hover:bg-gray-50/60 cursor-pointer transition-colors"
                    onClick={() => navigate(path)}>
                    <td className="px-4 py-3 text-gray-900 font-semibold">{name}</td>
                    {isReceivables && <td className="px-4 py-3 text-gray-500 text-xs">{phone || '—'}</td>}
                    {BUCKETS.map(b => {
                      const v = row.buckets?.[b.key] || 0;
                      return (
                        <td key={b.key} className={`px-4 py-3 text-right tabular-nums font-semibold ${v > 0 ? b.text : 'text-gray-300'}`}>
                          {v > 0 ? formatMoney(v) : '—'}
                        </td>
                      );
                    })}
                    <td className="px-4 py-3 text-right text-gray-900 font-extrabold tabular-nums">{formatMoney(row.total_outstanding)}</td>
                    {isReceivables && <td className="px-4 py-3 text-right text-gray-500 font-bold tabular-nums">{row.unpaid_invoice_count}</td>}
                    <td className="px-4 py-3 text-right">
                      <span className="text-gray-400 text-xs">›</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </GlassCard>
    </div>
  );
}
